import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { SubscriptionContext, accessFor } from './SubscriptionContext';
import api from '../services/Api';

export function SubscriptionProvider({ children }) {
    const { user } = useAuth();
    const accountId = user?.playerId;
    const { pathname } = useLocation();
    const roomId = pathname.match(/^\/play\/(\d+)\//)?.[1];
    const [data, setData] = useState(null);
    const [tick, setTick] = useState(Date.now());
    const [ready, setReady] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const offset = useRef(0);
    const dataRef = useRef(null);
    const epoch = useRef(0);
    const inFlight = useRef(null);
    const failures = useRef(0);
    const refresh = useCallback(async () => {
        if (document.visibilityState === 'hidden') return;
        if (inFlight.current?.epoch === epoch.current) return inFlight.current.promise;
        const current = epoch.current;
        const promise = (async () => {
            try {
                const result = (await api.get(accountId ? '/api/subscriptions/me' : '/api/subscriptions/public', accountId && roomId ? { _roomId: roomId } : { _anonymous: true })).data;
                if (current !== epoch.current) return;
                offset.current = Date.parse(result.serverUtcNow) - Date.now();
                failures.current = 0;
                dataRef.current = result;
                setData(result); setTick(Date.now() + offset.current); setUnavailable(false); setReady(true);
            } catch (_) {
                if (current !== epoch.current) return;
                failures.current = Math.min(failures.current + 1, 4);
                if (dataRef.current) return;
                // Account subscription details may fail independently from the
                // public switches. Keep routing usable while account data retries.
                try {
                    const fallback = (await api.get('/api/subscriptions/public', { _anonymous: true })).data;
                    if (current !== epoch.current) return;
                    offset.current = Date.parse(fallback.serverUtcNow) - Date.now();
                    dataRef.current = fallback;
                    setData(fallback); setTick(Date.now() + offset.current); setUnavailable(false); setReady(true);
                } catch (_) {
                    if (current === epoch.current && !dataRef.current) { setUnavailable(true); setReady(true); }
                }
            } finally {
                if (inFlight.current?.epoch === current) inFlight.current = null;
            }
        })();
        inFlight.current = { epoch: current, promise };
        return promise;
    }, [accountId, roomId]);
    useEffect(() => {
        epoch.current += 1;
        dataRef.current = null;
        setData(null); setReady(false); setUnavailable(false); failures.current = 0;
        let disposed = false;
        let timeout;
        const update = async () => {
            clearTimeout(timeout);
            await refresh();
            if (!disposed) timeout = setTimeout(update, failures.current
                ? Math.min(5000 * (2 ** (failures.current - 1)), 60000)
                : 60000);
        };
        const visible = () => { if (document.visibilityState !== 'hidden') void update(); };
        void update();
        window.addEventListener('pwf-subscription-changed', update);
        window.addEventListener('online', visible);
        document.addEventListener('visibilitychange', visible);
        return () => {
            disposed = true; epoch.current += 1; clearTimeout(timeout);
            window.removeEventListener('pwf-subscription-changed', update);
            window.removeEventListener('online', visible);
            document.removeEventListener('visibilitychange', visible);
        };
    }, [refresh]);
    useEffect(() => {
        // Only access boundaries update the shared room context. A countdown
        // must not rerender the entire card table or voice controls every second.
        const now = Date.now() + offset.current;
        const boundaries = [data?.subscription?.startsAt, data?.subscription?.endsAt]
            .map(value => Date.parse(value)).filter(value => Number.isFinite(value) && value > now);
        if (!boundaries.length) return undefined;
        const timeout = setTimeout(() => setTick(Date.now() + offset.current), Math.min(Math.min(...boundaries) - now + 1, 86400000));
        return () => clearTimeout(timeout);
    }, [data, tick]);
    const value = useMemo(() => ({ data, ready, unavailable, refresh, now: tick, clockOffset: offset.current,
        active: !!data?.subscription && !data.subscription.cancelled && Date.parse(data.subscription.startsAt) <= tick && Date.parse(data.subscription.endsAt) > tick,
        feature: (game, name) => !ready ? { show: true, enabled: false, allowed: false, locked: false }
            : accessFor(data, game, name, tick, !!user) }), [data, ready, unavailable, refresh, tick, user]);
    return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}
